import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import localforage from "localforage";
import { from, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { UserId } from "../interfaces";
import { WebServiceResponse } from "../interfaces/helpTarget";
import { LogClass, Logger } from "../logger";
import { convert, Substitute, SubstituteResponse } from "./models/substitute";

export abstract class AbstractSubstituteService {
  abstract getAll(): Observable<Substitute[]>;
  abstract getIsActive(userId: UserId): Observable<boolean>;
  abstract get(userId: UserId): Observable<Substitute>;
  abstract save(substitute: Substitute): Observable<Substitute>;
  abstract delete(userId: UserId): Observable<void>;
}

@LogClass()
@Injectable({
  providedIn: "root"
})
export class ProdSubstituteService implements AbstractSubstituteService {
  private substituteApi = "/api/substitutes";
  private logger: Logger;

  constructor(private http: HttpClient) {
    this.logger.debug("ctor");
  }

  save(substitute: Substitute): Observable<Substitute> {
    return this.http
      .post<WebServiceResponse<SubstituteResponse>>(
        `${this.substituteApi}/${substitute.userId}`,
        substitute,
        {
          responseType: "json"
        }
      )
      .pipe(map((res) => convert(res.data)));
  }
  get(userId: UserId): Observable<Substitute> {
    return this.http
      .get<WebServiceResponse<SubstituteResponse>>(
        `${this.substituteApi}/${userId}`,
        {
          responseType: "json"
        }
      )
      .pipe(map((res) => convert(res.data)));
  }

  getIsActive(userId: UserId): Observable<boolean> {
    return this.http
      .get<WebServiceResponse<boolean>>(
        `${this.substituteApi}/${userId}/active`,
        {
          responseType: "json"
        }
      )
      .pipe(map((res) => res.data));
  }

  delete(userId: UserId): Observable<void> {
    return this.http
      .delete<WebServiceResponse<void>>(`${this.substituteApi}/${userId}`, {
        responseType: "json"
      })
      .pipe(map((res) => res.data));
  }

  getAll(): Observable<Substitute[]> {
    return this.http
      .get<WebServiceResponse<SubstituteResponse[]>>(`${this.substituteApi}`, {
        responseType: "json"
      })
      .pipe(map((res) => res.data.map(convert)));
  }
}

@LogClass()
@Injectable({
  providedIn: "root"
})
export class MockSubstituteService implements AbstractSubstituteService {
  private logger: Logger;
  private db: LocalForage;

  constructor() {
    this.logger.debug("ctor");
    this.db = localforage.createInstance({ name: "substitutions" });
  }

  getAll(): Observable<Substitute[]> {
    return this.getAllItemsFromDb<Substitute>(this.db);
  }

  get(userId: UserId): Observable<Substitute> {
    return from(this.db.getItem<Substitute>(`${userId}`));
  }

  getIsActive(userId: UserId): Observable<boolean> {
    return this.get(userId).pipe(map((u) => !!u));
  }

  delete(userId: UserId): Observable<void> {
    this.logger.debug(`Delete substitute for ${userId}`);
    return from(this.db.removeItem(`${userId}`));
  }

  save(substitute: Substitute): Observable<Substitute> {
    return from(
      this.db.setItem<Substitute>(`${substitute.userId}`, substitute)
    );
  }

  private getAllItemsFromDb<T>(db: LocalForage) {
    return new Observable<T[]>((o) => {
      const all: T[] = [];
      db.iterate<T, void>((x) => {
        all.push(x);
        return undefined; // keep iterating
      })
        .then(() => {
          o.next(all);
          o.complete();
        })
        .catch((e) => {
          o.error(e);
        });
    });
  }
}
